import {
  FormRow,
  Input,
  Spacer,
  Stack,
  Switch,
  Typography,
  Wizard,
  useWizardPage,
  Link,
  Paragraph,
  Title
} from '@efast_public/fjd-component-library';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFitConnectClientInformationUpdate } from '../../../../api/hooks/useFitconnectSenderInformation';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useQuery } from '@tanstack/react-query';
import { ClientInformationDto } from '../../../../api/models/FitConnect/FitConnect';
import { BehApiRoutes } from '../../../../api/beh-api';
import {
  FITCONNECT_SSP_SENDER_CLIENTS,
  FJD_DOCS_EINRICHTUNGSASSISTANT
} from '../../../../shared/utils/Links';

interface SenderConfigurationForm {
  clientId: string;
  clientSecret: string;
}

function SenderConfiguration(): ReactElement {
  const { t } = useTranslation();
  const { mutateAsync: updateFitConnectClientInformation } =
    useFitConnectClientInformationUpdate();

  const { data } = useQuery<ClientInformationDto>({
    queryKey: [BehApiRoutes.fitConnectSenderInformation()]
  });

  const {
    formState: { errors, isValid },
    register,
    handleSubmit,
    reset,
    trigger
  } = useForm<SenderConfigurationForm>({
    mode: 'onChange',
    defaultValues: {
      clientId: '',
      clientSecret: ''
    },
    resolver: joiResolver(
      Joi.object<SenderConfigurationForm>({
        clientId: Joi.string()
          .required()
          .messages({
            'string.empty': t('string.empty')
          }),
        clientSecret: Joi.string()
          .required()
          .messages({
            'string.empty': t('string.empty')
          })
      })
    )
  });

  useEffect(() => {
    if (data) {
      reset(data);
      trigger().catch(() => {});
    }
  }, [data, reset, trigger]);

  const onSubmit = useCallback(async () => {
    const isFormValid = await trigger();
    if (!isFormValid) {
      return Promise.reject();
    }

    return handleSubmit((state) => {
      return updateFitConnectClientInformation(state);
    })();
  }, [handleSubmit, trigger, updateFitConnectClientInformation]);

  const { wizardState } = useWizardPage({
    isFormValid: isValid,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    isStepOptional: true,
    skipButtonLabel: 'Später einrichten',
    primaryButtonLabel: t('createAndUpdateMandantCredsModal.connect.button'),
    onSubmit
  });

  return (
    <Wizard.Page>
      <Stack spacing="m">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.senderConfig.headline')}
        </Title>
        <Paragraph>
          <Trans
            i18nKey="createAndUpdateMandantCredsModal.subline"
            components={{
              Link: (
                <Link href={FITCONNECT_SSP_SENDER_CLIENTS} target="_blank">
                  link
                </Link>
              )
            }}
          />
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link href={FJD_DOCS_EINRICHTUNGSASSISTANT} target="_blank">
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        <Spacer size="s" />

        <form>
          <Stack spacing="m" direction="column">
            <FormRow
              label={t('createAndUpdateMandantCredsModal.clientId.label')}
              validationMessage={errors.clientId?.message}
              variant={errors.clientId?.message ? 'error' : undefined}
            >
              <Input
                id="clientId"
                {...register('clientId')}
                type="text"
                placeholder={t(
                  'createAndUpdateMandantCredsModal.clientId.placeholder'
                )}
              />
            </FormRow>
            <FormRow
              label={t('createAndUpdateMandantCredsModal.clientSecret.label')}
              validationMessage={errors.clientSecret?.message}
              variant={errors.clientSecret?.message ? 'error' : undefined}
            >
              <Input
                enablePasswordToggle
                placeholder={t(
                  'createAndUpdateMandantCredsModal.clientSecret.placeholder'
                )}
                id="clientSecret"
                {...register('clientSecret')}
                type="password"
              />
            </FormRow>
          </Stack>
        </form>
        <Spacer size="3xl" />
      </Stack>
    </Wizard.Page>
  );
}

export default SenderConfiguration;
